import React, { useState, useEffect } from 'react';
import {
  sendSignInLinkToEmail,
  isSignInWithEmailLink,
  signInWithEmailLink,
  signInWithEmailAndPassword
} from 'firebase/auth';
import { auth } from '../firebase';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  Alert,
  CircularProgress
} from '@mui/material';

const LOGIN_WITH_PASSWORD = process.env.REACT_APP_LOGIN_WITH_PASSWORD === 'true'; // Controlled via environment variable

const Login: React.FC = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [sent, setSent] = useState(false);
  const [checkingLink, setCheckingLink] = useState(true);

  useEffect(() => {
    const completeSignIn = async () => {
      if (isSignInWithEmailLink(auth, window.location.href)) {
        setCheckingLink(true);
        let emailForSignIn = window.localStorage.getItem('emailForSignIn');

        if (!emailForSignIn) {
          emailForSignIn = window.prompt('Please provide your email for confirmation');
        }

        if (emailForSignIn) {
          try {
            setLoading(true);
            await signInWithEmailLink(auth, emailForSignIn, window.location.href);
            window.localStorage.removeItem('emailForSignIn');
            // User will be automatically updated via AuthContext/onAuthStateChanged
          } catch (err: any) {
            setError(err.message || 'Failed to sign in with link');
            setLoading(false);
          }
        }
      }
      setCheckingLink(false);
    };

    completeSignIn();
  }, []);

  const handleSendLink = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email) return;

    setLoading(true);
    setError(null);

    const actionCodeSettings = {
      url: window.location.origin, // Returns to the base URL
      handleCodeInApp: true,
    };

    try {
      await sendSignInLinkToEmail(auth, email, actionCodeSettings);
      window.localStorage.setItem('emailForSignIn', email);
      setSent(true);
    } catch (err: any) {
      setError(err.message || 'Failed to send sign-in link');
    } finally {
      setLoading(false);
    }
  };

  const handlePasswordLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email || !password) return;

    setLoading(true);
    setError(null);

    try {
      await signInWithEmailAndPassword(auth, email, password);
    } catch (err: any) {
      let errorMessage = 'Failed to sign in';
      if (err.code === 'auth/wrong-password') errorMessage = 'סיסמה שגויה';
      else if (err.code === 'auth/user-not-found') errorMessage = 'משתמש לא נמצא';
      else if (err.code === 'auth/invalid-email') errorMessage = 'אימייל לא תקין';

      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  if (!LOGIN_WITH_PASSWORD && (checkingLink || (loading && !sent))) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', height: '100vh', gap: 2 }}>
        <CircularProgress />
        <Typography>מתחבר...</Typography>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh',
        bgcolor: 'background.default',
      }}
    >
      <Paper
        elevation={3}
        sx={{
          p: 4,
          maxWidth: 400,
          width: '100%',
          textAlign: 'center',
          borderRadius: 2,
        }}
      >
        <Typography variant="h5" gutterBottom sx={{ mb: 3 }}>
          כניסה למערכת
        </Typography>

        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

        {LOGIN_WITH_PASSWORD ? (
          <form onSubmit={handlePasswordLogin}>
            <TextField
              fullWidth
              label="אימייל"
              variant="outlined"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              disabled={loading}
              sx={{ mb: 2 }}
              required
            />
            <TextField
              fullWidth
              label="סיסמה"
              variant="outlined"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              disabled={loading}
              sx={{ mb: 3 }}
              required
            />
            <Button
              fullWidth
              variant="contained"
              color="primary"
              type="submit"
              size="large"
              disabled={loading || !email || !password}
            >
              {loading ? <CircularProgress size={24} color="inherit" /> : 'התחבר'}
            </Button>
          </form>
        ) : (
          sent ? (
            <Box>
              <Alert severity="success" sx={{ mb: 2 }}>
                קישור התחברות נשלח לכתובת {email}
              </Alert>
              <Typography variant="body2">
                אנא בדוק את תיבת המייל שלך ולחץ על הקישור כדי להיכנס.
              </Typography>
              <Button
                variant="text"
                onClick={() => setSent(false)}
                sx={{ mt: 2 }}
              >
                שלח שוב
              </Button>
            </Box>
          ) : (
            <form onSubmit={handleSendLink}>
              <TextField
                fullWidth
                label="אימייל"
                variant="outlined"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                disabled={loading}
                sx={{ mb: 3 }}
                required
              />
              <Button
                fullWidth
                variant="contained"
                color="primary"
                type="submit"
                size="large"
                disabled={loading || !email}
              >
                {loading ? <CircularProgress size={24} color="inherit" /> : 'שלח קישור התחברות'}
              </Button>
            </form>
          )
        )}
      </Paper>
    </Box>
  );
};

export default Login;
